import IBusinessLocation from '@/app/(auth)/signup/types/IBusinessLocation';

/**
 * Node in the graph of businesses. Contains any child businesses that belong to this business.
 */
export interface IBusinessGraphNode {
  businessId: number;
  parentBusinessId?: number;
  children: IBusinessGraphNode[];
  name: string;
  isRoot: boolean;
}

/**
 * Pick out only the bits of the business that we need for t.Dhe graph.
 *
 * This makes it much easier to test the graph building code as we don't need to construct a full business object.
 */
export type IBusiness = Pick<IBusinessLocation, 'Id' | 'RootBusinessId' | 'Name'>;

/**
 * Node in the graph of businesses. Contains any child businesses that belong to this business.
 *
 * Don't construct this directly, use the static method buildBusinessGraphs() passing the list of businesses into it.
 */
export class BusinessGraphNode implements IBusinessGraphNode {
  businessId: number;
  parentBusinessId?: number;
  children: IBusinessGraphNode[];
  business: IBusiness;
  name: string;
  isRoot: boolean;

  constructor(business: IBusiness, children: IBusinessGraphNode[]) {
    this.business = business;
    this.businessId = business.Id;
    this.children = children;
    this.parentBusinessId = business.RootBusinessId || undefined;
    this.name = business.Name;
    this.isRoot = !business.RootBusinessId;
  }

  static buildBusinessGraph(forBusiness: IBusiness, businesses: IBusiness[]): IBusinessGraphNode {
    const directChildren = businesses.filter(b => b.RootBusinessId === forBusiness.Id);

    // We can recurse down into the businesses and find *their* children
    return new BusinessGraphNode(
      forBusiness,
      directChildren.map(c => BusinessGraphNode.buildBusinessGraph(c, businesses)),
    );
  }

  static buildBusinessGraphs(businesses: IBusiness[]): IBusinessGraphNode[] {
    // Find any root businesses first
    const roots = businesses.filter(b => !b.RootBusinessId);

    return roots.map(root => BusinessGraphNode.buildBusinessGraph(root, businesses));
  }
}
